/* eslint-disable eslint-comments/no-restricted-disable -- Temporarily disabling to allow max-lines override below */
/* eslint-disable max-lines -- File slightly exceeds limit due to necessary inline documentation; extracting would reduce clarity */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isBuyNowPayLater } from "@/react/helpers/paywallCheckoutHelpers";

import type { StripeElementsMode } from "../../StripeElements/types";
import {
  captureExternalTransactionToken,
  clearExternalTransactionToken,
} from "./helpers/externalTransactionToken";
import { useExpressMode } from "./hooks/useExpressMode";
import { useSubmitAttempt } from "./hooks/useSubmitAttempt";
import { useVerifyAttempt } from "./hooks/useVerifyAttempt";
import { PaywallCheckoutAttemptsContext } from "./PaywallCheckoutAttemptsContext";
import { PaywallCheckoutAttemptsStageHandler } from "./PaywallCheckoutAttemptsStageHandler";
import type {
  BasePaywallCheckoutAttemptsContextProviderProps,
  MemberAddressState,
  OnAddressChange,
  OnCheckoutTermsChanged,
  OnComplete,
  OnError,
  OnPaymentMethodChange,
  OnProgress,
  OnStageChangeProps,
  PaywallCheckoutAttemptsMode,
} from "./types";
import { stages, type Stage } from "./types";

const isProgressStage = (stage: Stage): boolean =>
  stage !== stages.completed &&
  stage !== stages.failed &&
  stage !== stages.idle;

export interface PaywallCheckoutAttemptsContextProviderProps extends BasePaywallCheckoutAttemptsContextProviderProps {
  isMemberAddressRequired: boolean;
  /** Derived from orderPriceType: true for subscription/installments prices */
  isOrderRecurring: boolean;
  isPaymentMethodRequired: boolean;
  /** "default": standard flow; "express": saved payment method only (upsells) */
  mode: PaywallCheckoutAttemptsMode;
  onAddressChange?: OnAddressChange;
  onCheckoutTermsChanged?: OnCheckoutTermsChanged;
  onComplete: OnComplete;
  onError: OnError;
  onPaymentMethodChange?: OnPaymentMethodChange;
  onProgress: OnProgress;
  stripeElementsMode: StripeElementsMode;
}

export const PaywallCheckoutAttemptsContextProvider = ({
  children,
  ...props
}: PaywallCheckoutAttemptsContextProviderProps) => {
  const {
    checkoutIntentToken,
    communityId,
    communityProcessorPaymentMethodConfigurationId,
    doesCommunityProcessorSupportsLink,
    doesMemberHasDefaultPaymentMethod,
    isMemberAddressComplete,
    isMemberAddressRequired,
    isOrderRecurring,
    isPaymentMethodRequired,
    memberAddressCity,
    memberAddressCountry,
    memberAddressLine1,
    memberAddressLine2,
    memberAddressPostalCode,
    memberAddressState,
    memberEmail,
    memberName,
    memberProcessorCustomerSessionClientSecret,
    mode,
    onAddressChange,
    onComplete,
    onCheckoutTermsChanged,
    onError,
    onPaymentMethodChange,
    onProgress,
    orderAmount,
    orderCurrencyMinimumChargeAmount,
    paywallId,
    paywallPriceId,
    sessionId,
    sessionStepId,
    stripeElementsMode,
  } = props;
  const isZeroAmount = orderAmount < orderCurrencyMinimumChargeAmount;
  // Derived from isOrderRecurring: used by useSubmitAttempt → useConfirmPayment to gate
  // setup_future_usage for Klarna on one-time purchases.
  const isOneTimePrice = !isOrderRecurring;
  // Captured once per mount (lazy initializer): URL on the initial load, sessionStorage
  // on any later mount within the same tab/session (e.g. post-redirect return, or an
  // upsell OfferPage mounting its own provider). See helpers/externalTransactionToken.
  const [externalTransactionToken] = useState(() =>
    captureExternalTransactionToken(),
  );
  const [paymentMethodType, setPaymentMethodType] = useState<string>("");
  const [
    isMemberUsingDefaultPaymentMethod,
    setIsMemberUsingDefaultPaymentMethod,
  ] = useState(false);
  const [isPaymentMethodComplete, setIsPaymentMethodComplete] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  // Tracks whether Stripe's onChange has fired at least once. This is separate from
  // isPaymentElementReady (onReady) because these callbacks can fire in either order.
  const [isPaymentMethodLoaded, setIsPaymentMethodLoaded] = useState(false);
  const [memberAddress, setMemberAddress] = useState<MemberAddressState>({
    city: memberAddressCity,
    country: memberAddressCountry,
    email: memberEmail,
    isComplete: isMemberAddressComplete,
    line1: memberAddressLine1,
    line2: memberAddressLine2,
    name: memberName,
    postalCode: memberAddressPostalCode,
    state: memberAddressState,
  });

  // Sync memberName/memberEmail props into memberAddress when they change after mount
  // (e.g. user authenticates via OTP after the provider has mounted with undefined values).
  // Name uses !prev.name guard (AddressElement manages name). Email has no guard because
  // the AddressElement doesn't manage email — the prop is always authoritative.
  useEffect(() => {
    setMemberAddress(prev => {
      const nextName = memberName && !prev.name ? memberName : prev.name;
      const nextEmail = memberEmail || prev.email;
      if (nextName !== prev.name || nextEmail !== prev.email) {
        return { ...prev, name: nextName, email: nextEmail };
      }
      return prev;
    });
  }, [memberName, memberEmail]);

  // Address element required if consumer requires it OR payment method is BNPL (Klarna, Affirm, etc.)
  const isAddressElementRequired = useMemo(
    () => isMemberAddressRequired || isBuyNowPayLater(paymentMethodType),
    [isMemberAddressRequired, paymentMethodType],
  );

  // Sync address changes to consumer
  useEffect(() => {
    onAddressChange?.({
      ...memberAddress,
      isComplete: !isAddressElementRequired || memberAddress.isComplete,
    });
  }, [isAddressElementRequired, memberAddress, onAddressChange]);

  // Sync payment method changes to consumer
  useEffect(() => {
    onPaymentMethodChange?.({
      isComplete: isPaymentMethodComplete,
      isDefaultPaymentMethod: isMemberUsingDefaultPaymentMethod,
      paymentMethodType,
    });
  }, [
    isMemberUsingDefaultPaymentMethod,
    isPaymentMethodComplete,
    onPaymentMethodChange,
    paymentMethodType,
  ]);

  /**
   * Stage Management: Ref + State Pattern
   *
   * We maintain stage in both a ref and state for different purposes:
   *
   * - `stageRef`: Enables synchronous reads in callbacks. React's `useCallback` captures
   *   state values at creation time (closure), so callbacks would see stale `stage` values.
   *   The ref provides an always-current value for guard checks like `canSubmitAttempt()`.
   *
   * - `stage` state: Drives React re-renders so the UI updates when stage changes.
   *
   * Both are updated atomically in `handleStageChange`. This is safe from race conditions
   * because JavaScript is single-threaded: the ref update happens synchronously before
   * any `await` yields control to the event loop, so subsequent guard checks see the
   * updated value immediately.
   */
  const stageRef = useRef<Stage>(stages.idle);
  const [stage, setStage] = useState<Stage>(stageRef.current);

  const handleStageChange = useCallback(
    (stageChangeProps: OnStageChangeProps) => {
      const { stage: nextStage } = stageChangeProps;
      stageRef.current = nextStage;
      setStage(nextStage);

      if (isProgressStage(nextStage)) {
        onProgress?.(nextStage);
        return;
      }

      if (nextStage === stages.completed) {
        // Checkout is done -- clear so a later, unrelated checkout in the same
        // tab never inherits this token (see helpers/externalTransactionToken).
        clearExternalTransactionToken();
        const {
          attemptId,
          attemptStatus,
          invoiceId,
          paymentIntentId,
          paymentMethodType,
          subscriptionId,
          userSignedIn: isUserSignedIn,
        } = stageChangeProps;
        void onComplete?.({
          attemptId,
          attemptStatus,
          invoiceId,
          paymentIntentId,
          paymentMethodType,
          subscriptionId,
          userSignedIn: isUserSignedIn,
        });
        return;
      }

      if (nextStage === stages.failed) {
        onError?.({ error: stageChangeProps.error });
      }
    },
    [onComplete, onError, onProgress],
  );

  // Express mode: validates and handles saved payment method checkout (upsells, one-click purchases)
  const { isExpressMode } = useExpressMode({
    memberProcessorCustomerSessionClientSecret,
    mode,
  });

  /**
   * Guard function for submission. Checks both ref and state to ensure:
   * 1. The ref reflects the true current stage (for async callback contexts)
   * 2. The state matches (ensuring React has caught up with the latest render)
   *
   * Returns true only when in `idle` (fresh attempt) or `failed` (retry after error).
   * This prevents duplicate submissions: once `submitAttempt` starts, the stage
   * transitions to `settingUpPaymentMethod` or `creatingAttempt` synchronously,
   * causing subsequent calls to return early.
   */
  const canSubmitAttempt = useCallback(
    () =>
      (stageRef.current === stages.idle && stage === stages.idle) ||
      (stageRef.current === stages.failed && stage === stages.failed),
    [stage],
  );

  const submitAttempt = useSubmitAttempt({
    canSubmitAttempt,
    checkoutIntentToken,
    communityId,
    communityProcessorPaymentMethodConfigurationId,
    doesCommunityProcessorSupportsLink,
    doesMemberHasDefaultPaymentMethod,
    externalTransactionToken,
    isExpressMode,
    isMemberUsingDefaultPaymentMethod,
    isOneTimePrice,
    isPaymentMethodRequired,
    memberAddress,
    stripeElementsMode,
    memberProcessorCustomerSessionClientSecret,
    onStageChange: handleStageChange,
    paymentMethodType,
    paywallPriceId,
    sessionId,
    sessionStepId,
  });

  /**
   * Guard function for verification. Allows verification in two scenarios:
   *
   * 1. `idle`: The expected case. After payment confirmation, the user is redirected
   *    back to the checkout page. On page load, the provider is in `idle` state and
   *    URL parameters trigger verification. This is the standardized "redirect-all"
   *    flow used for ALL payment methods (cards, bank transfers, etc.).
   *
   * 2. `confirmingAttempt`: For internal navigation without page reload. This includes
   *    trial/$0 orders (no payment to confirm) and successful payment confirmations
   *    that don't require redirect (e.g., card payments with `redirect: "if_required"`).
   *
   * Same ref+state pattern as canSubmitAttempt for race condition safety.
   */
  const canVerifyAttempt = useCallback(
    () =>
      (stageRef.current === stages.idle && stage === stages.idle) ||
      (stageRef.current === stages.confirmingAttempt &&
        stage === stages.confirmingAttempt),
    [stage],
  );

  const verifyAttempt = useVerifyAttempt({
    canVerifyAttempt,
    onCheckoutTermsChanged,
    onStageChange: handleStageChange,
    paywallId,
    sessionId,
  });

  const isProcessingAttempt = isProgressStage(stage);

  return (
    <PaywallCheckoutAttemptsContext.Provider
      value={{
        ...props,
        canVerifyAttempt,
        isAddressElementRequired,
        isExpressMode,
        isMemberUsingDefaultPaymentMethod,
        isOrderRecurring,
        isPaymentElementReady,
        isPaymentMethodComplete,
        isPaymentMethodLoaded,
        isProcessingAttempt,
        isZeroAmount,
        memberAddress,
        onStageChange: handleStageChange,
        paymentMethodType,
        setIsPaymentMethodLoaded,
        setIsMemberUsingDefaultPaymentMethod,
        setIsPaymentElementReady,
        setIsPaymentMethodComplete,
        setMemberAddress,
        setPaymentMethodType,
        submitAttempt,
        verifyAttempt,
      }}
    >
      {children}
      <PaywallCheckoutAttemptsStageHandler />
    </PaywallCheckoutAttemptsContext.Provider>
  );
};
